/**
 * DiffView — unified diff renderer for file changes
 *
 * Renders a stored unified diff's hunks — added/removed/context lines with
 * line numbers, a separator wherever unchanged lines were left out.
 *
 * Takes the DIFF, not the two files (#768): edit tools store a trimmed unified
 * diff (`core/filediff.ts`), and a row written before that is diffed on read by
 * `diffTextOf` — the one place the legacy before/after shape is read.
 *
 * Used inside ToolCallBlock to display edit/write results.
 */
import { useMemo } from 'react'
import { diffWords, parsePatch } from 'diff'

/* ------------------------------------------------------------------ */
/*  DiffBadge — compact +N/-N pill for collapsed tool call headers    */
/* ------------------------------------------------------------------ */

export interface DiffBadgeProps {
  additions: number
  deletions: number
}

export function DiffBadge({ additions, deletions }: DiffBadgeProps) {
  if (additions === 0 && deletions === 0) return null

  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono">
      {additions > 0 && (
        <span className="px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-400">
          +{additions}
        </span>
      )}
      {deletions > 0 && (
        <span className="px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400">
          -{deletions}
        </span>
      )}
    </span>
  )
}

/* ------------------------------------------------------------------ */
/*  DiffView — full unified diff with line numbers                    */
/* ------------------------------------------------------------------ */

interface DiffViewProps {
  /** A unified diff for one file (`createTwoFilesPatch` shape). */
  diff: string
  file?: string
  maxHeight?: number
}

/** A word-level segment within a changed line */
interface WordSegment {
  text: string
  highlight: boolean
}

/**
 * A single display line in the unified diff output.
 * `type` drives the visual styling (added/removed/context/separator).
 * `words` is set on modified lines for word-level highlighting.
 */
export interface DiffLine {
  type: 'added' | 'removed' | 'context' | 'separator'
  text: string
  oldNum?: number
  newNum?: number
  words?: WordSegment[]
}

/**
 * The display lines for a unified diff: each hunk's lines, numbered from its
 * header, with a separator wherever the diff left unchanged lines out (before a
 * hunk that does not start at line 1, and between hunks). The context kept
 * around each change is whatever the diff was written with (3 lines — see
 * `fileDiff`).
 *
 * `\ No newline at end of file` markers are dropped: they annotate the line
 * before them and are not content.
 */
export function buildDiffLines(diff: string): {
  lines: DiffLine[]
  additions: number
  deletions: number
} {
  const lines: DiffLine[] = []
  let additions = 0
  let deletions = 0
  if (!diff) return { lines, additions, deletions }
  let patches: ReturnType<typeof parsePatch>
  try {
    patches = parsePatch(diff)
  } catch {
    // A malformed stored diff renders as nothing rather than breaking the
    // message list. The +/- counts on the part still render in the header.
    return { lines, additions, deletions }
  }
  for (const patch of patches) {
    patch.hunks.forEach((hunk, h) => {
      if (h > 0 || hunk.oldStart > 1 || hunk.newStart > 1) lines.push({ type: 'separator', text: '' })
      let oldNum = hunk.oldStart
      let newNum = hunk.newStart
      for (const raw of hunk.lines) {
        const marker = raw[0]
        const text = raw.slice(1)
        if (marker === '+') {
          lines.push({ type: 'added', text, newNum: newNum++ })
          additions++
        } else if (marker === '-') {
          lines.push({ type: 'removed', text, oldNum: oldNum++ })
          deletions++
        } else if (marker === ' ') {
          lines.push({ type: 'context', text, oldNum: oldNum++, newNum: newNum++ })
        }
      }
    })
  }

  // Compute word-level highlights for modified lines (adjacent removed→added pairs)
  annotateWordDiffs(lines)

  return { lines, additions, deletions }
}

/**
 * Find adjacent removed/added line pairs and annotate them with word-level diffs.
 * A "pair" is a contiguous block of N removed lines followed by N added lines —
 * these represent modifications rather than pure inserts/deletes.
 */
function annotateWordDiffs(lines: DiffLine[]): void {
  let i = 0
  while (i < lines.length) {
    // Find a run of removed lines
    const removeStart = i
    while (i < lines.length && lines[i].type === 'removed') i++
    const removeEnd = i

    // Find a run of added lines immediately after
    const addStart = i
    while (i < lines.length && lines[i].type === 'added') i++
    const addEnd = i

    const removeCount = removeEnd - removeStart
    const addCount = addEnd - addStart

    // Only pair up if both sides exist — pair min(removeCount, addCount) lines
    const pairs = Math.min(removeCount, addCount)
    for (let p = 0; p < pairs; p++) {
      const oldLine = lines[removeStart + p]
      const newLine = lines[addStart + p]
      const changes = diffWords(oldLine.text, newLine.text)

      oldLine.words = []
      newLine.words = []
      for (const c of changes) {
        if (c.removed) {
          oldLine.words.push({ text: c.value, highlight: true })
        } else if (c.added) {
          newLine.words.push({ text: c.value, highlight: true })
        } else {
          oldLine.words.push({ text: c.value, highlight: false })
          newLine.words.push({ text: c.value, highlight: false })
        }
      }
    }

    // Skip past non-removed/non-added lines
    if (i === removeStart) i++
  }
}

export function DiffView({ diff, file, maxHeight = 300 }: DiffViewProps) {
  const { lines, additions, deletions } = useMemo(() => buildDiffLines(diff), [diff])

  // Nothing to show if identical
  if (lines.length === 0) return null

  // Compute gutter width from max line number
  const maxLineNum = lines.reduce((max, l) => {
    return Math.max(max, l.oldNum ?? 0, l.newNum ?? 0)
  }, 0)
  const gutterWidth = String(maxLineNum).length

  return (
    <div className="rounded-md overflow-hidden border themed-border my-1">
      {/* Header */}
      <div className="px-3 py-1.5 themed-bg-tertiary border-b themed-border flex items-center gap-2">
        {file && (
          <span className="text-xs font-mono themed-text-muted truncate flex-1">{file}</span>
        )}
        {!file && <span className="flex-1" />}
        <DiffBadge additions={additions} deletions={deletions} />
      </div>

      {/* Diff lines */}
      <div
        className="overflow-y-auto overflow-x-auto bg-black/20"
        style={{ maxHeight }}
      >
        <table className="w-full text-xs font-mono border-collapse">
          <tbody>
            {lines.map((line, i) => {
              if (line.type === 'separator') {
                return (
                  <tr key={i} className="bg-blue-500/5">
                    <td
                      colSpan={3}
                      className="px-3 py-0.5 text-center text-[10px] themed-text-muted select-none"
                    >
                      &middot;&middot;&middot;
                    </td>
                  </tr>
                )
              }

              const prefix = line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '
              const bgClass =
                line.type === 'added'
                  ? 'bg-green-500/15'
                  : line.type === 'removed'
                    ? 'bg-red-500/15'
                    : ''
              const textClass =
                line.type === 'added'
                  ? 'text-green-300'
                  : line.type === 'removed'
                    ? 'text-red-300'
                    : 'themed-text'

              return (
                <tr key={i} className={bgClass}>
                  {/* Old line number */}
                  <td className="px-1.5 py-0 text-right themed-text-muted select-none opacity-50 align-top" style={{ minWidth: `${gutterWidth + 1}ch` }}>
                    {line.oldNum ?? ''}
                  </td>
                  {/* New line number */}
                  <td className="px-1.5 py-0 text-right themed-text-muted select-none opacity-50 border-r themed-border align-top" style={{ minWidth: `${gutterWidth + 1}ch` }}>
                    {line.newNum ?? ''}
                  </td>
                  {/* Content */}
                  <td className={`px-2 py-0 whitespace-pre ${textClass}`}>
                    {prefix}{line.words ? (
                      line.words.map((seg, j) =>
                        seg.highlight ? (
                          <span
                            key={j}
                            className={line.type === 'added' ? 'bg-green-500/35 rounded-sm' : 'bg-red-500/35 rounded-sm'}
                          >
                            {seg.text}
                          </span>
                        ) : seg.text
                      )
                    ) : line.text}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
